/**
 * Words and judgements for the Health room, kept apart from the element so
 * node tests can import them (`argus-health-room.ts`).
 */

import type { ArgusHealth, ArgusHealthBacktest } from '../../api/types.ts';

/** A live job waiting this long is the `argus-live-waiting` alarm. */
export const LIVE_LATE_S = 300;

/** 42 s, 3 min, 2.5 h, 4 d. */
export function ago(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '-';
  const s = Math.max(0, Math.round(seconds));
  if (s < 90) return `${s} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  if (s < 172_800) return `${Math.round(s / 360) / 10} h`;
  return `${Math.round(s / 86_400)} d`;
}

/**
 * A job's duration, precise enough to compare workers: 0.41 s, 2.74 s,
 * 42.1 s, 3 min 24 s. Past an hour it is `ago`.
 */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '-';
  const s = Math.max(0, seconds);
  if (s < 10) return `${s.toFixed(2)} s`;
  if (s < 90) return `${s.toFixed(1)} s`;
  if (s < 3600) {
    const m = Math.floor(Math.round(s) / 60);
    const rest = Math.round(s) % 60;
    return rest ? `${m} min ${rest} s` : `${m} min`;
  }
  return ago(s);
}

/** Problems worth the chip at the top, in words. Empty means all well. */
export function problems(h: ArgusHealth): string[] {
  const out: string[] = [];
  if (!h.schedulers.some((s) => s.silent_s <= h.alive_seconds)) out.push('no scheduler reports');
  const gone = h.pools.flatMap((p) => p.workers).filter((w) => !w.alive);
  if (gone.length) out.push(`${gone.length} worker${gone.length > 1 ? 's' : ''} gone`);
  const failed = h.queues.reduce((n, q) => n + q.failed_15m, 0);
  if (failed) out.push(`${failed} job${failed > 1 ? 's' : ''} failed in 15 min`);
  const live = h.queues.find((q) => q.name === 'live');
  if (live && live.oldest_wait_s > LIVE_LATE_S) out.push(`a Live job waits ${ago(live.oldest_wait_s)}`);
  const late = h.data.reduce((n, d) => n + d.late.length, 0);
  if (late) out.push(`${late} ship${late > 1 ? 's' : ''} late`);
  return out;
}

/** The window of the queue cards' done and failed numbers, in minutes: Explore's Live chips. */
export const QUEUE_CHIPS = [
  { value: 5, label: '5m', title: 'Last 5 minutes' },
  { value: 15, label: '15m', title: 'Last 15 minutes' },
  { value: 60, label: '1h', title: 'Last hour' },
  { value: 360, label: '6h', title: 'Last 6 hours' },
  { value: 1440, label: '24h', title: 'Last 24 hours' },
] as const;
export const QUEUE_MORE = [
  { value: 30, label: '30m', title: 'Last 30 minutes' },
  { value: 120, label: '2h', title: 'Last 2 hours' },
  { value: 180, label: '3h', title: 'Last 3 hours' },
  { value: 720, label: '12h', title: 'Last 12 hours' },
] as const;

/** The window of "What it did", in hours (backend `MAX_STATS_HOURS` is 720). */
export const STATS_CHIPS = [
  { value: 1, label: '1h', title: 'Last hour' },
  { value: 6, label: '6h', title: 'Last 6 hours' },
  { value: 24, label: '24h', title: 'Last 24 hours' },
  { value: 168, label: '7d', title: 'Last 7 days' },
  { value: 720, label: '30d', title: 'Last 30 days' },
] as const;
export const STATS_MORE = [
  { value: 3, label: '3h', title: 'Last 3 hours' },
  { value: 12, label: '12h', title: 'Last 12 hours' },
  { value: 48, label: '48h', title: 'Last 48 hours' },
  { value: 336, label: '14d', title: 'Last 14 days' },
] as const;

/** A window in minutes, in words: 15 min, 1 h, 7 d. */
export function windowWords(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 2880 || minutes % 1440) return `${Math.round(minutes / 6) / 10} h`;
  return `${minutes / 1440} d`;
}

/** Hours per bar, so a window draws at most `most` bars: 1 for a day, 6 for 30 days at 120. */
export function hoursPerBar(hours: number, most: number): number {
  return Math.max(1, Math.ceil(hours / most));
}

/**
 * The change against the window before, in whole percent, and whether it is
 * good: more jobs is good, a longer wait is not. Null when there is nothing
 * to compare with.
 */
export function change(now: number | null | undefined, before: number | null | undefined, upIsGood: boolean):
  { pct: number; tone: 'good' | 'bad' | 'flat' } | null {
  if (now === null || now === undefined || before === null || before === undefined || before === 0) return null;
  const pct = Math.round((100 * (now - before)) / before);
  if (Math.abs(pct) < 2) return { pct: 0, tone: 'flat' };
  return { pct, tone: pct > 0 === upIsGood ? 'good' : 'bad' };
}

/** A backtest's window in words: "30 d", "all time", "2026-09-01 to 2026-10-01". */
export function backtestWindow(b: Pick<ArgusHealthBacktest, 'days' | 'range_from' | 'range_to' | 'all_time'>): string {
  if (b.all_time) return 'all time';
  if (b.range_from && b.range_to) return `${b.range_from.slice(0, 10)} to ${b.range_to.slice(0, 10)}`;
  return `${b.days} d`;
}

/**
 * Seconds a running backtest still needs, from its pace so far; null until it
 * finished a first ship, or when the total is not known.
 */
export function secondsLeft(done: number, total: number | null, claimedAt: string | null, now: string): number | null {
  if (!claimedAt || !total || done <= 0) return null;
  const spent = (Date.parse(now) - Date.parse(claimedAt)) / 1000;
  return Math.max(0, (spent * (total - done)) / done);
}
